import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import spIcon from "../../assets/brand/swlogo.png";
import spWordmark from "../../assets/brand/swletters.png";
import api from "../../api/api";
import socket from "../../socket";
import { toast } from "react-toastify";
import { useAuth } from "../../context/useAuth";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password || !isChecked) {
      toast.warn("Please fill in all fields and accept the terms");
      return;
    }

    try {
      setSubmitting(true);

      const data = await api.auth.login(email, password);

      if (data?.user?.role !== "vendor") {
        toast.warn("You are not a vendor");
        return;
      }

      const mergedUser = login(data.token, data.user);

      if (!mergedUser) {
        toast.error("Failed to authenticate user.");
        return;
      }

      if (remember) localStorage.setItem("sp_remember", "1");
      else localStorage.removeItem("sp_remember");

      if (mergedUser?.userId || data.user?._id) {
        socket.emit("registerUser", { 
          userId: mergedUser.userId || data.user._id 
        });
      }

      toast.success("Login successful!");
      navigate("/dashboard");

    } catch (err) {
      console.warn("Login failed:", err);
      toast.warn(err?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fff8e1] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md mx-auto">
        
        {/* Logo Section */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-1">
            <img
              src={spIcon}
              alt="Sweet Plate logo"
              className="w-24 h-20 object-contain -mt-1"
            />
            <img
              src={spWordmark}
              alt="Sweet Plate"
              className="h-12 object-contain -ml-2"
            />
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#5d4037] leading-tight">
            Welcome Back
          </h1>
          <p className="mt-3 text-[#5d4037] text-base md:text-lg leading-relaxed max-w-[340px] mx-auto">
            Ready to serve again? Log in to update your menu and connect with customers.
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Email Field */}
          <div>
            <label className="block text-base font-semibold text-[#5d4037] mb-3">
              Email address
            </label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-14 bg-white border border-[#5d4037] rounded-2xl px-5 text-base outline-none focus:border-[#f7c945] transition-colors"
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-base font-semibold text-[#5d4037] mb-3">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-14 bg-white border border-[#5d4037] rounded-2xl pl-5 pr-14 text-base outline-none focus:border-[#f7c945] transition-colors"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-[#5d4037]"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5 16.477 5 20.268 7.943 21.542 12 20.268 16.057 16.477 19 12 19 7.523 19 3.732 16.057 2.458 12z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908l3.42 3.42M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember Me + Forgot Password */}
          <div className="flex items-center justify-between text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-5 h-5 rounded border-[#5d4037] accent-[#f7c945]"
              />
              <span className="text-[#5d4037]">Remember me</span>
            </label>

            <Link 
              to="/forgot-password" 
              className="text-[#663114] hover:underline font-medium"
            >
              Forgot Password?
            </Link>
          </div>

          {/* Terms Checkbox */}
          <div className="flex gap-3 pt-2">
            <input
              id="login-terms"
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-[#5d4037] accent-[#f7c945]"
            />
            <label htmlFor="login-terms" className="text-sm text-[#62552c] leading-relaxed">
              I certify that I am 18 years of age or older, and I agree to the{" "}
              <span className="text-[#663114] underline cursor-pointer">User Agreement</span> and{" "}
              <span className="text-[#663114] underline cursor-pointer">Privacy Policy</span>.
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className={`mt-4 w-full h-14 rounded-full font-bold text-lg transition-all duration-200
              ${submitting 
                ? "bg-[#d9c48c] cursor-not-allowed" 
                : "bg-[#f7c945] hover:bg-[#f5b800] active:scale-[0.985]"
              }`}
          >
            {submitting ? "Signing in..." : "Login"}
          </button>

          {/* Register Link */}
          <p className="text-center mt-6 text-base text-[#5d4037]">
            Haven't registered yet?{" "}
            <Link 
              to="/signup" 
              className="font-semibold text-[#663114] underline hover:text-[#8c4a1f]"
            >
              Register here
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};